import {
  useCandidateLog,
  useUpdateCandidate,
  useUploadCandidateAttachment,
} from "@vidario/api-client";
import {
  formatDateDE,
  movedBackEntry,
  safeHttpUrl,
  type Candidate,
  type CandidateLogEntry,
} from "@vidario/domain";
import { Button, Card, HiddenBlock, Input, Label, useToast } from "@vidario/ui";
import {
  Download,
  File,
  Image as ImageIcon,
  Mail,
  MessageSquare,
  Pencil,
  Upload,
} from "lucide-react";
import { useRef, useState, type ChangeEvent, type ReactElement } from "react";

import { ConfirmOpenButton, attachmentConfirm } from "../../anonym/ConfirmOpenButton";
import { useAnonymView } from "../../anonym/useAnonymView";

const ACTION_LABELS: Record<CandidateLogEntry["action"], string> = {
  status_change: "Status geändert",
  note_recruiter: "Recruiter-Notiz",
  note_admin_internal: "Admin-Notiz",
  note_interview: "Interview-Notiz",
  imported: "Hinzugefügt zur Longlist",
  reject: "Abgelehnt",
  unreject: "Ablehnung aufgehoben",
  outreach_start: "Outreach gestartet",
  moved_back: "Zurück in die Longlist",
  skill_match_override: "Skill-Match angepasst",
  attachment_upload: "Anhang hochgeladen",
  source_profile_opened: "Originalprofil in der Anonym-Ansicht geöffnet",
  attachment_opened: "Anhang in der Anonym-Ansicht geöffnet",
};

export interface ContactSidebarProps {
  jobId: string;
  candidate: Candidate;
  /** Admins can edit contact data + attachments inline. */
  isAdmin: boolean;
  /** A closed job's candidates are read-only apart from notes (#19). */
  readOnly?: boolean;
}

/** Right-side sidebar — Kontakt + Anhänge + Aktivität (MVP §4.4 + §4.5
 * + §9.2 audit trail). Admins get inline-edit for contact data and
 * attachment URLs via the view-until-click pattern. */
export function ContactSidebar({
  jobId,
  candidate,
  isAdmin,
  readOnly = false,
}: ContactSidebarProps): ReactElement {
  const { data: log = [] } = useCandidateLog(candidate.id);
  const anonym = useAnonymView();

  return (
    <div className="flex flex-col gap-3">
      <ContactCard jobId={jobId} candidate={candidate} isAdmin={isAdmin && !readOnly} />
      <AttachmentsCard jobId={jobId} candidate={candidate} isAdmin={isAdmin && !readOnly} />
      <Card>
        <h4 className="m-0 mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Aktivität
        </h4>
        {anonym && log.length > 0 && (
          <p className="m-0 mb-2.5 text-xs text-text-tertiary">
            Notizen zu Aktivitäten sind verborgen.
          </p>
        )}
        {log.length === 0 ? (
          <div className="text-[13px] italic text-text-tertiary">Keine Aktivität.</div>
        ) : (
          <ol
            aria-label="Aktivitäts-Log"
            data-testid="activity-log"
            className="m-0 flex flex-col gap-2.5 list-none p-0"
          >
            {log.slice(0, 6).map((entry) => (
              <li
                key={entry.id}
                className="border-l-2 border-neutral-200 pl-3 text-xs leading-snug"
              >
                <LogEntryText entry={entry} anonym={anonym} />
                <div className="text-text-tertiary">{formatDateDE(entry.createdAt)}</div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function LogEntryText({
  entry,
  anonym,
}: {
  entry: CandidateLogEntry;
  anonym: boolean;
}): ReactElement {
  if (entry.action === "moved_back") {
    // The stage it came from, and the reason from the dialog on its own line (#162).
    const { label, reason } = movedBackEntry(entry.note);
    return (
      <div className="text-text-primary">
        {label}
        {reason && !anonym && (
          <div className="whitespace-pre-line text-text-secondary">{reason}</div>
        )}
      </div>
    );
  }
  return (
    <div className="text-text-primary">
      {ACTION_LABELS[entry.action]}
      {entry.note && !anonym ? `: ${entry.note}` : ""}
    </div>
  );
}

function ContactCard({ jobId, candidate, isAdmin }: ContactSidebarProps): ReactElement {
  const anonym = useAnonymView();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    email: candidate.email ?? "",
    phone: candidate.phone ?? "",
    salaryExpectation: candidate.salaryExpectation ?? "",
    availableFrom: candidate.availableFrom ?? "",
  });
  const update = useUpdateCandidate();
  const toast = useToast();
  // Sanitise the mailto: target — the email is scraped external data and
  // React does not sanitise href. Renders as plain text when unsafe.
  const mailtoHref = candidate.email ? safeHttpUrl(`mailto:${candidate.email}`) : undefined;

  const save = (): void => {
    update.mutate(
      {
        jobId,
        candidateId: candidate.id,
        patch: {
          email: draft.email || null,
          phone: draft.phone || null,
          salaryExpectation: draft.salaryExpectation || null,
          availableFrom: draft.availableFrom || null,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.push({
            kind: "success",
            title: "Kontakt gespeichert",
            message: "Kontaktdaten aktualisiert.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancel = (): void => {
    setDraft({
      email: candidate.email ?? "",
      phone: candidate.phone ?? "",
      salaryExpectation: candidate.salaryExpectation ?? "",
      availableFrom: candidate.availableFrom ?? "",
    });
    setEditing(false);
  };

  if (anonym) {
    // No contact data, no editing; an open draft is kept for later (#140).
    return (
      <Card>
        <h4 className="m-0 mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Kontakt
        </h4>
        {editing ? (
          <HiddenBlock>Bearbeiten ist in der Anonym-Ansicht gesperrt.</HiddenBlock>
        ) : (
          <div className="flex flex-col gap-1.5 text-[13px]">
            <AnonymField label="E-Mail" />
            <AnonymField label="Telefon" />
            {candidate.salaryExpectation && (
              <div className="mt-2 border-t border-border-default pt-2">
                <div className="text-text-tertiary">Gehaltswunsch</div>
                <div className="font-semibold">{candidate.salaryExpectation}</div>
              </div>
            )}
            {candidate.availableFrom && (
              <div>
                <div className="text-text-tertiary">Verfügbar ab</div>
                <div className="font-semibold">{candidate.availableFrom}</div>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="m-0 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Kontakt
        </h4>
        {isAdmin && !editing && <EditTrigger onClick={() => setEditing(true)} />}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2.5">
          <Field id="contact-email" label="E-Mail">
            <Input
              id="contact-email"
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              placeholder="name@example.com"
            />
          </Field>
          <Field id="contact-phone" label="Telefon">
            <Input
              id="contact-phone"
              type="tel"
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              placeholder="+49 …"
            />
          </Field>
          <Field id="contact-salary" label="Gehaltswunsch">
            <Input
              id="contact-salary"
              value={draft.salaryExpectation}
              onChange={(e) => setDraft({ ...draft, salaryExpectation: e.target.value })}
              placeholder="z.B. 85.000 €"
            />
          </Field>
          <Field id="contact-available" label="Verfügbar ab">
            <Input
              id="contact-available"
              type="date"
              value={draft.availableFrom}
              onChange={(e) => setDraft({ ...draft, availableFrom: e.target.value })}
            />
          </Field>
          <EditActions onCancel={cancel} onSave={save} saving={update.isPending} />
        </div>
      ) : (
        <>
          {candidate.email ? (
            <div className="mb-2 flex items-center gap-2 text-[13px]">
              <Mail size={14} className="shrink-0 text-text-tertiary" />
              {mailtoHref ? (
                <a
                  href={mailtoHref}
                  // An address has no spaces to wrap at (#124).
                  className="min-w-0 break-all text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {candidate.email}
                </a>
              ) : (
                <span className="min-w-0 break-all text-text-primary">{candidate.email}</span>
              )}
            </div>
          ) : (
            <div className="mb-2 text-[13px] italic text-text-tertiary">
              Keine E-Mail hinterlegt.
            </div>
          )}
          {candidate.phone && (
            <div className="flex items-center gap-2 text-[13px]">
              <MessageSquare size={14} className="text-text-tertiary" />
              <span className="tabular-nums">{candidate.phone}</span>
            </div>
          )}
          {(candidate.salaryExpectation || candidate.availableFrom) && (
            <div className="mt-3 border-t border-border-default pt-3 text-[13px]">
              {candidate.salaryExpectation && (
                <div>
                  <div className="text-text-tertiary">Gehaltswunsch</div>
                  <div className="font-semibold">{candidate.salaryExpectation}</div>
                </div>
              )}
              {candidate.availableFrom && (
                <div className="mt-2">
                  <div className="text-text-tertiary">Verfügbar ab</div>
                  <div className="font-semibold">{candidate.availableFrom}</div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function AttachmentsCard({ jobId, candidate, isAdmin }: ContactSidebarProps): ReactElement {
  const anonym = useAnonymView();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    cvUrl: candidate.cvUrl ?? "",
    photoUrl: candidate.photoUrl ?? "",
  });
  const update = useUpdateCandidate();
  const upload = useUploadCandidateAttachment();
  const toast = useToast();

  const save = (): void => {
    update.mutate(
      {
        jobId,
        candidateId: candidate.id,
        patch: {
          cvUrl: draft.cvUrl || null,
          photoUrl: draft.photoUrl || null,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.push({
            kind: "success",
            title: "Anhänge gespeichert",
            message: "Anhang-Links aktualisiert.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancel = (): void => {
    setDraft({
      cvUrl: candidate.cvUrl ?? "",
      photoUrl: candidate.photoUrl ?? "",
    });
    setEditing(false);
  };

  const handleUpload = (kind: "cv" | "photo", file: File): void => {
    upload.mutate(
      { jobId, candidateId: candidate.id, kind, file },
      {
        onSuccess: () =>
          toast.push({
            kind: "success",
            title: kind === "cv" ? "Lebenslauf hochgeladen" : "Foto hochgeladen",
            message: file.name,
          }),
        onError: (err) =>
          toast.push({ kind: "error", title: "Upload fehlgeschlagen", message: err.message }),
      },
    );
  };

  if (anonym) {
    // Type labels, not file names; opening asks first and is logged; no upload (#140).
    return (
      <Card>
        <h4 className="m-0 mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Anhänge
        </h4>
        {editing ? (
          <HiddenBlock>Bearbeiten ist in der Anonym-Ansicht gesperrt.</HiddenBlock>
        ) : (
          <div className="flex flex-col gap-2.5 text-[13px]">
            <AnonymAttachment
              candidateId={candidate.id}
              url={candidate.cvUrl}
              kind="cv"
              label="Lebenslauf"
            />
            <AnonymAttachment
              candidateId={candidate.id}
              url={candidate.photoUrl}
              kind="photo"
              label="Foto"
            />
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="m-0 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Anhänge
        </h4>
        {isAdmin && !editing && <EditTrigger onClick={() => setEditing(true)} />}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2.5">
          <Field id="att-cv" label="Lebenslauf-URL">
            <Input
              id="att-cv"
              type="url"
              value={draft.cvUrl}
              onChange={(e) => setDraft({ ...draft, cvUrl: e.target.value })}
              placeholder="https://…/lebenslauf.pdf"
            />
          </Field>
          <Field id="att-photo" label="Foto-URL">
            <Input
              id="att-photo"
              type="url"
              value={draft.photoUrl}
              onChange={(e) => setDraft({ ...draft, photoUrl: e.target.value })}
              placeholder="https://…/foto.jpg"
            />
          </Field>
          <EditActions onCancel={cancel} onSave={save} saving={update.isPending} />
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <AttachmentRow
            label="Lebenslauf"
            icon={<File size={14} className="text-neutral-500" />}
            href={candidate.cvUrl}
            kind="cv"
            accept="application/pdf"
            onUpload={(file) => handleUpload("cv", file)}
            uploading={upload.isPending && upload.variables?.kind === "cv"}
          />
          <AttachmentRow
            label="Foto"
            icon={<ImageIcon size={14} className="text-neutral-500" />}
            href={candidate.photoUrl}
            kind="photo"
            accept="image/jpeg,image/png,image/webp"
            onUpload={(file) => handleUpload("photo", file)}
            uploading={upload.isPending && upload.variables?.kind === "photo"}
          />
        </div>
      )}
    </Card>
  );
}

function AttachmentRow({
  label,
  icon,
  href,
  kind,
  accept,
  onUpload,
  uploading,
}: {
  label: string;
  icon: ReactElement;
  href: string | null;
  kind: "cv" | "photo";
  accept: string;
  onUpload: (file: File) => void;
  uploading: boolean;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  // Sanitise the stored attachment URL before it reaches an href — cvUrl /
  // photoUrl are scraped/admin-entered external data. When unsafe it's
  // treated as "no attachment": plain text + upload prompt, no live link.
  const safeHref = safeHttpUrl(href);

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    // Reset so picking the same filename a second time still fires onChange.
    e.target.value = "";
  };

  return (
    <div className="flex items-center gap-2 rounded-md border border-border-default px-2.5 py-2 text-[13px]">
      {icon}
      {safeHref ? (
        <a
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 truncate text-text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {label} öffnen
        </a>
      ) : (
        <span className="flex-1 italic text-text-tertiary">{label}: nicht hinterlegt</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        aria-label={`${label} hochladen`}
        className="hidden"
        onChange={onChange}
        data-testid={`attachment-input-${kind}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[12px] font-medium text-primary-600 hover:underline disabled:cursor-default disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        title={safeHref ? `${label} ersetzen` : `${label} hochladen`}
      >
        <Upload size={12} /> {uploading ? "Lädt…" : safeHref ? "Ersetzen" : "Hochladen"}
      </button>
      {safeHref && (
        <a
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-neutral-400 hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label={`${label} herunterladen`}
        >
          <Download size={13} />
        </a>
      )}
    </div>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactElement;
}): ReactElement {
  return (
    <div>
      <Label htmlFor={id} className="mb-1 block text-[11px] font-medium text-text-tertiary">
        {label}
      </Label>
      {children}
    </div>
  );
}

function EditTrigger({ onClick }: { onClick: () => void }): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <Pencil size={12} /> Bearbeiten
    </button>
  );
}

function EditActions({
  onCancel,
  onSave,
  saving,
}: {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}): ReactElement {
  return (
    <div className="mt-1 flex justify-end gap-2">
      <Button variant="secondary" size="sm" onClick={onCancel}>
        Abbrechen
      </Button>
      <Button size="sm" loading={saving} onClick={onSave}>
        Speichern
      </Button>
    </div>
  );
}

/** A contact field in the Anonym-Ansicht: always „verborgen", never a hint at the value. */
function AnonymField({ label }: { label: string }): ReactElement {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-text-tertiary">{label}</span>
      <span className="italic text-text-tertiary">verborgen</span>
    </div>
  );
}

function AnonymAttachment({
  candidateId,
  url,
  kind,
  label,
}: {
  candidateId: string;
  url: string | null;
  kind: "cv" | "photo";
  label: string;
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-2">
      <span>{label}</span>
      {url ? (
        <ConfirmOpenButton
          url={url}
          candidateId={candidateId}
          action="attachment_opened"
          kind={kind}
          {...attachmentConfirm(label)}
          aria-label={`${label} öffnen`}
          className="cursor-pointer text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Öffnen
        </ConfirmOpenButton>
      ) : (
        <span className="italic text-text-tertiary">nicht hinterlegt</span>
      )}
    </div>
  );
}
